import { Box, Card, CardContent, GridLegacy, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  EXPLORER_GENERAL_DOCUMENTS,
  EXPLORER_WHITELABEL_DOCUMENTS,
  explorerDocumentPublicUrl,
  type ExplorerDocumentItem,
} from 'src/constants/mindtime/explorerDocuments';
import {
  EXPLORER_PC_BACKUP_INSTALLERS,
  EXPLORER_PRO_VM_BACKUP_INSTALLERS,
  type ExplorerInstallerEntry,
} from 'src/constants/mindtime/explorerInstallers';
import {
  ExplorerPlatformIcon,
  ExplorerProductLogo,
  type ExplorerProductLogoKey,
} from 'src/mindtime/icons/explorer/ExplorerPlatformIcons';

// const MANUALS_KB_URL = 'https://www.mindtimebackup.eu/knowledgebase/';

/** Aligns product titles with installer icon tiles (centered in minWidth 88 cards). */
const PRODUCT_HEADER_LEFT_INSET = '20px';

const ProductSectionHeader = ({
  label,
  product,
  mb = 2,
}: {
  label: string;
  product: ExplorerProductLogoKey;
  mb?: number;
}) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1}
    mb={mb}
    sx={{ pl: PRODUCT_HEADER_LEFT_INSET }}
  >
    <ExplorerProductLogo product={product} />
    <Typography variant="subtitle2" fontWeight={700} fontSize={14} sx={{ color: '#1A202C' }}>
      {label}
    </Typography>
  </Box>
);

const SectionHeader = ({
  label,
  icon,
}: {
  label: string;
  icon?: React.ReactNode;
}) => (
  <Box display="flex" alignItems="center" gap={1} mb={2}>
    {icon}
    <Typography variant="subtitle2" fontWeight={700} fontSize={14} sx={{ color: '#1A202C' }}>
      {label}
    </Typography>
  </Box>
);

const InstallerCard = ({
  ariaLabel,
  entry,
}: {
  ariaLabel: string;
  entry: ExplorerInstallerEntry;
}) => (
  <Box
    component="a"
    href={entry.downloadUrl}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 88,
      maxWidth: 100,
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
      '&:hover': { opacity: 0.7 },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
        borderRadius: 1,
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        bgcolor: '#EDF2F7',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ExplorerPlatformIcon platform={entry.platform} />
    </Box>
    <Typography variant="caption" fontSize={11} color="textSecondary" textAlign="center">
      {entry.label}
    </Typography>
  </Box>
);

const DocumentRow = ({ item }: { item: ExplorerDocumentItem }) => (
  <Box
    component="a"
    href={explorerDocumentPublicUrl(item.slug)}
    download={item.label}
    rel="noopener noreferrer"
    aria-label={`Download ${item.label}`}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      py: 1,
      px: 0.5,
      borderBottom: '1px solid #EDF2F7',
      textDecoration: 'none',
      color: 'inherit',
      cursor: 'pointer',
      '&:hover': { bgcolor: '#F7FAFC' },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
      },
    }}
  >
    <PictureAsPdfIcon sx={{ fontSize: 18, color: '#E53E3E', flexShrink: 0 }} />
    <Typography
      component="span"
      variant="body2"
      fontSize={12}
      color="textSecondary"
      sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word', pr: 1 }}
    >
      {item.label}
    </Typography>
    <Typography
      component="span"
      variant="body2"
      fontSize={12}
      color="textSecondary"
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {item.size}
    </Typography>
  </Box>
);

const DownloadsContent = () => (
  <GridLegacy container spacing={3}>
    <GridLegacy item xs={12} md={7}>
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Online Installer
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              bgcolor: '#E6FFFA',
              border: '1px solid #B2F5EA',
              borderRadius: '6px',
              px: 2,
              py: 1,
              mb: 3,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: '#38B2AC' }} />
            <Typography variant="body2" fontSize={13} color="textSecondary">
              Small installer that downloads required components
            </Typography>
          </Box>

          <ProductSectionHeader label="PC Backup" product="pcBackup" />
          <Box display="flex" flexWrap="wrap" gap={3} mb={3}>
            {EXPLORER_PC_BACKUP_INSTALLERS.map((f) => (
              <InstallerCard
                key={f.id}
                entry={f}
                ariaLabel={`Download PC Backup for ${f.label}`}
              />
            ))}
          </Box>

          <Box display="flex" flexWrap="wrap" alignItems="center" gap={3} mb={2}>
            <ProductSectionHeader label="Pro Backup" product="proBackup" mb={0} />
            <ProductSectionHeader label="VM Backup" product="vmBackup" mb={0} />
          </Box>
          <Box display="flex" flexWrap="wrap" gap={3}>
            {EXPLORER_PRO_VM_BACKUP_INSTALLERS.map((f) => (
              <InstallerCard
                key={f.id}
                entry={f}
                ariaLabel={`Download Pro Backup / VM Backup for ${f.label}`}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </GridLegacy>

    <GridLegacy item xs={12} md={5}>
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Documents
          </Typography>

          <GridLegacy container spacing={2}>
            <GridLegacy item xs={12} sm={6}>
              <SectionHeader
                label="General"
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 20, color: '#1A202C' }} />}
              />
              {EXPLORER_GENERAL_DOCUMENTS.map((item) => (
                <DocumentRow key={item.id} item={item} />
              ))}
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <SectionHeader
                label="White label"
                icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20, color: '#1A202C' }} />}
              />
              {EXPLORER_WHITELABEL_DOCUMENTS.map((item) => (
                <DocumentRow key={item.id} item={item} />
              ))}
            </GridLegacy>
          </GridLegacy>

          {/* <Box mt={3}>
            <SectionHeader
              label="Manuals"
              icon={<DescriptionOutlinedIcon sx={{ fontSize: 20, color: '#1A202C' }} />}
            />
            <Box mt={1}>
              <Typography
                component="a"
                href={MANUALS_KB_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                fontSize={13}
                color="textSecondary"
                sx={{
                  wordBreak: 'break-all',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                www.mindtimebackup.eu/knowledgebase/
              </Typography>
            </Box>
          </Box> */}
        </CardContent>
      </Card>
    </GridLegacy>
  </GridLegacy>
);

export default DownloadsContent;
